import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

/*
 * Google Analytics, and the banner that has to come before it — owner decision,
 * 2026-08-10.
 *
 * ---
 *
 * **Nothing in this file does anything until a measurement ID is set.** With
 * `VITE_GA_MEASUREMENT_ID` empty — which is every environment today, and every
 * developer's machine forever — this renders `null`, loads no script, sets no
 * cookie and shows no banner. The site is byte-for-byte what it was. That is
 * deliberate: the feature was built before the ID existed, and a half-configured
 * analytics integration that pops a consent banner at customers while measuring
 * nothing would be worse than not having it.
 *
 * **Why a banner at all, when the site's own counter needs none.** They collect
 * differently and the law follows the collection, not the purpose. Our counter
 * hashes a visitor into a handle that changes at midnight and stores no
 * identifier; Google sets a cookie in the visitor's browser and keeps it for two
 * years. Storing something on somebody's device to recognise them later is the
 * thing consent regimes are about, so Google waits to be asked and our own
 * counter does not.
 *
 * **Consent is asked once and the answer is respected in both directions.** A
 * decline is stored exactly as firmly as an acceptance — a banner that reappears
 * every visit until somebody gives in is not consent, and re-asking is the most
 * common way these are built wrong. Clearing site data is how a visitor changes
 * their mind, which is what clearing site data means everywhere else too.
 *
 * **`localStorage`, and CLAUDE.md §4 is not being bent.** That rule is about
 * *tokens* — an access token in `localStorage` is one XSS away from a permanent
 * account takeover (P21). This stores the string `granted` or `denied`. Stealing
 * it accomplishes nothing and forging it accomplishes less.
 */

const STORAGE_KEY = 'honeycomb.analytics-consent';
const MEASUREMENT_ID: string = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';

type Consent = 'granted' | 'denied';

function storedConsent(): Consent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    // Safari in private mode throws on `localStorage`. No stored answer means
    // the banner shows, which is the safe direction: ask rather than assume.
    return null;
  }
}

/**
 * Load gtag.js and start the session.
 *
 * **Only ever called after a `granted` answer**, so there is no "consent mode"
 * dance here — the script is not on the page at all until somebody has said yes,
 * which is simpler than loading it denied and upgrading it later, and leaves
 * nothing to get wrong.
 */
function loadGoogleAnalytics(): void {
  if (document.getElementById('ga-script') !== null) return;

  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  const globalWindow = window as unknown as { dataLayer?: unknown[] };
  globalWindow.dataLayer ??= [];

  function gtag(...args: unknown[]): void {
    globalWindow.dataLayer?.push(args);
  }

  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, {
    // Google's own switch for not keeping the last octet of the address. It
    // costs nothing and it is the setting somebody would ask about.
    anonymize_ip: true,
  });
}

export function CookieConsent() {
  /*
   * **Read at first render, not in an effect.** There is no server rendering
   * here, so the stored answer is available before the first paint — and taking
   * it in an effect would flash the banner at somebody who answered it months
   * ago, which is the exact thing this component exists not to do.
   */
  const [consent, setConsent] = useState<Consent | null>(() =>
    MEASUREMENT_ID === '' ? null : storedConsent(),
  );

  // Loading a script is the side effect, so it is the only thing in the effect.
  useEffect(() => {
    if (MEASUREMENT_ID !== '' && consent === 'granted') loadGoogleAnalytics();
  }, [consent]);

  // Nothing configured, or already answered — either way there is no question
  // to ask. A decline is remembered exactly as firmly as an acceptance.
  if (MEASUREMENT_ID === '' || consent !== null) return null;

  function answer(value: Consent) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Private browsing. The answer holds for this visit and is asked again
      // next time, which is the only thing a browser refusing storage allows.
    }
    setConsent(value);
  }

  return (
    <div
      // `role="dialog"` rather than an alert: it is a question, and it does not
      // trap focus or block the page. Somebody who wants to read about a house
      // clean can ignore this entirely and it will still be there.
      role="dialog"
      aria-labelledby="cookie-consent-heading"
      aria-describedby="cookie-consent-body"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-ink-300 bg-white p-4 shadow-card sm:p-6"
    >
      <div className="mx-auto flex w-full max-w-content flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="cookie-consent-heading" className="text-label text-ink-900">
            Cookies on this site
          </h2>
          <p id="cookie-consent-body" className="mt-1 max-w-2xl text-body text-ink-500">
            We&rsquo;d like to use Google Analytics to understand how people find and use our
            website. It sets a cookie in your browser. Everything on the site works the same either
            way.
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {/*
            **Decline is first and looks like a real button**, not a grey link
            beside a bright Accept. A choice presented that way is not much of a
            choice, and the business does not need the visits badly enough to
            play that game.
          */}
          <Button
            variant="outline"
            onClick={() => {
              answer('denied');
            }}
          >
            No thanks
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              answer('granted');
            }}
          >
            That&rsquo;s fine
          </Button>
        </div>
      </div>
    </div>
  );
}
